// src/components/secciones/InsertarSecciones.tsx

import { safeFetch } from "@/api/safeFetch";
import { ButtonSiguiente, Input } from "@/components/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const InsertarSecciones: React.FC = () => {
  const [nombreSeccion, setNombreSeccion] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState<File | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();



  // 👉 Manejar imagen seleccionada
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setFoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };


  // 👉 Enviar datos al backend
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!nombreSeccion || !descripcion || !foto) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("nombreSeccion", nombreSeccion);
      formData.append("descripcion", descripcion);
      formData.append("foto_perfil", foto);

      const res = await safeFetch("/api/insertaSecciones", {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Error al insertar sección");

      setSuccess("Sección creada exitosamente 🎉");
      setNombreSeccion("");
      setDescripcion("");
      setFoto(null);
      setPreview(null);

    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocurrió un error.");
      }
    }

  };


  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);


  useEffect(() => {
    return () => {
      // liberar blob del preview
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }

      // limpiar input file
      const inputs = document.querySelectorAll<HTMLInputElement>('input[type="file"]');
      inputs.forEach(input => (input.value = ""));

      // limpiar estados
      setNombreSeccion("");
      setDescripcion("");
      setFoto(null);
      setPreview(null);
      setError("");
      setSuccess("");
      setLoading(false);
    };
  }, []);


  return (
    <div className="w-full h-screen flex items-center justify-center bg-zinc-500 relative px-4">

      {/* 🔙 Botón arriba izquierda (fuera del card) */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/backoffice")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      {/* 🟦 Contenedor centrado */}
      <div className="w-full dark:text-zinc-700 max-w-2xl p-6 bg-zinc-400 shadow-md rounded-xl shadow-yellow-600 border-zinc-700 border-r-4 border-t-2">

        <h2 className="text-2xl font-bold mb-4 text-center">
          Insertar Nueva Sección
        </h2>

        {error && <p className="text-red-600 mb-2">{error}</p>}
        {success && <p className="text-green-600 mb-2">{success}</p>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-6 dark:text-zinc-700">

          {/* 🔹 Nombre */}
          <div>
            <label className="font-medium">Nombre de la Sección</label>
            <Input
              type="text"
              className="w-full border p-2 rounded mt-1"
              value={nombreSeccion}
              onChange={(e) => setNombreSeccion(e.target.value)}
            />
          </div>

          {/* 🔹 Descripción */}
          <div>
            <label className="font-medium">Descripción</label>
            <textarea
              className="w-full border border-zinc-400 p-2 rounded-lg shadow-md mt-1 min-h-[120px] dark:bg-white"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          {/* 🔹 Imagen — SEPARADO */}
          <div className="flex items-center justify-between gap-6">
            {/* Input personalizado */}
            <div className="flex-1 flex flex-col">
              <label className="font-medium mb-2">Imagen de la sección</label>

              <div className="flex items-center gap-4">
                {/* Botón de selección de imagen */}
                <label className="flex items-center justify-center py-2 h-8 w-3/6 bg-zinc-600 rounded-md border-white border text-white cursor-pointer hover:text-zinc-800 hover:bg-gray-100 hover:border-2 hover:border-blue-700 transition">
                  <span className="font-semibold">Seleccionar Imagen</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>

                {/* Mostrar nombre del archivo al lado del botón */}
                {foto && (
                  <span className="text-sm text-gray-600 truncate">{foto.name}</span>
                )}
              </div>

            </div>

            {/* Preview centrada */}
            {preview && (
              <div className="flex-none flex justify-center w-1/3">
                <img
                  src={preview}
                  className="w-20 h-20 object-cover rounded-lg shadow-lg border"
                  alt="Preview"
                />
              </div>
            )}
          </div>


          {/* Guardar */}
          <ButtonSiguiente
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
          >
            {loading ? "Guardando..." : "Guardar Sección"}
          </ButtonSiguiente>

        </form>
      </div>
    </div>
  );


};

export default InsertarSecciones;
